import React, { useEffect } from 'react';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { getClientStream } from '@confluence/ssr-data-streaming/entry-points/client.ts';
import type { BoundaryError } from '@confluence/ssr-data-streaming/entry-points/error.ts';

import { QueryBoundaryErrorMessage } from './QueryBoundaryErrorMessage.tsx';

class BoundaryErrorHandler {
	private errorStream?: ReturnType<typeof getClientStream<BoundaryError>>;

	constructor(private flags: WithFlagsProps['flags']) {
		if (window.__SSR_REACT_STREAMING__) {
			this.errorStream = getClientStream<BoundaryError>('query-boundary-errors');
			this.errorStream.addListener(this.handleBoundaryError);
		}
	}

	public cleanup = () => {
		if (this.errorStream) {
			this.errorStream.removeListener(this.handleBoundaryError);
		}
	};

	private handleBoundaryError = (error: BoundaryError) => {
		const flagId = `query-boundary-${error.errorType}-${error.operationName}`;
		let flagTitle = null;
		switch (error.errorType) {
			case 'outside-boundary':
				flagTitle = 'Query Used Outside Suspense Boundary!';
				break;
			case 'boundary-mismatch':
				flagTitle = 'Query Boundary Mismatch!';
				break;
		}

		if (flagTitle) {
			void this.flags.showErrorFlag({
				id: flagId,
				title: flagTitle,
				isAutoDismiss: false,
				description: <QueryBoundaryErrorMessage {...error} />,
			});
		}
	};
}

export const QueryBoundaryNotificationComponent: React.ComponentType<Omit<unknown, 'flags'>> =
	withFlags(({ flags }: WithFlagsProps) => {
		useEffect(() => {
			if (!window.__SSR_REACT_STREAMING__) {
				return;
			}

			// Create the handler which will automatically set up the listener
			const handler = new BoundaryErrorHandler(flags);
			return () => {
				handler.cleanup();
			};
		}, [flags]);

		return null;
	});

export const QueryBoundaryNotification = (): React.JSX.Element | null => {
	if (
		process.env.CLOUD_ENV === 'dev' ||
		process.env.CLOUD_ENV === 'branch' ||
		process.env.CLOUD_ENV === 'staging'
	) {
		return (
			<TransparentErrorBoundary attribution={Attribution.BACKBONE}>
				<QueryBoundaryNotificationComponent />
			</TransparentErrorBoundary>
		);
	}
	return null;
};
